import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { AUTHORS } from "@/data/authors";
import SEO from "@/components/SEO";

const FOUNDER = AUTHORS["shriya-das"];
const MARKETING = AUTHORS["tarini-seth"];

// Document fragments that scatter → converge into a network.
// xFrom/yFrom/xTo/yTo are in PIXELS relative to the container centre.
const FRAGMENTS = [
  { id: 1, label: "MISSION",    xFrom: -420, yFrom: -180, rotFrom: -14, xTo: -220, yTo:  -80 },
  { id: 2, label: "RESEARCH",   xFrom:  380, yFrom: -200, rotFrom:  10, xTo:  220, yTo:  -80 },
  { id: 3, label: "OPEN",       xFrom: -440, yFrom:  140, rotFrom:  16, xTo: -220, yTo:   80 },
  { id: 4, label: "GLOBAL",     xFrom:  420, yFrom:  180, rotFrom: -12, xTo:  220, yTo:   80 },
  { id: 5, label: "SIGNALS",    xFrom:  -60, yFrom: -260, rotFrom:   0, xTo:    0, yTo: -140 },
  { id: 6, label: "DIGNITY",    xFrom:   60, yFrom:  240, rotFrom:   4, xTo:    0, yTo:  140 },
];

const NODE_POINTS = [
  [-220, -80], [220, -80], [-220, 80], [220, 80], [0, -140], [0, 140],
];

const VALUES = [
  { code: "01", t: "Independence", d: "No affiliations, no compromises. IOCRI reports to the public." },
  { code: "02", t: "Rigour",       d: "Cited sources, transparent methods, peer-reviewed briefs." },
  { code: "03", t: "Human Dignity",d: "Victims and communities lead our editorial choices." },
  { code: "04", t: "Openness",     d: "Every source is public, every finding is free to share." },
];

const TIMELINE = [
  ["2026 · Q1", "IOCRI founded by Shriya Das to make research on organized crime accessible worldwide."],
  ["2026 · Q2", "First open-access brief on trade-based money laundering published."],
  ["2026 · Q3", "AI Crime Assistant launched with citation-linked responses."],
  ["2026 · Q4", "Global partnership network expands to 42 countries."],
];

export default function About() {
  const sceneRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sceneRef,
    offset: ["start start", "end start"],
  });

  // 0 → scattered, 1 → converged network
  const converge = useTransform(scrollYProgress, [0, 0.85], [0, 1]);
  const linkOpacity = useTransform(converge, [0, 0.5, 1], [0, 0.15, 0.8]);
  const captionOpacity = useTransform(converge, [0, 0.4, 1], [0, 0, 1]);

  return (
    <div data-testid="about-page">
      <SEO
        title="About IOCRI"
        description="The International Organized Crime Research Institute is an independent research network mapping global organized crime through open knowledge and AI."
        path="/about" image="institute"
      />

      {/* ============================================================= */}
      {/* SCROLL-CAMERA HERO — fragmented documents → connected network */}
      {/* ============================================================= */}
      <section
        ref={sceneRef}
        className="relative h-[200vh]"
        data-testid="about-hero-scene"
      >
        <div className="sticky top-0 h-screen flex items-center justify-center hero-radial overflow-hidden">
          {/* Ambient traces */}
          <svg viewBox="0 0 800 500" className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <path className="trace-cyan"    d="M120 100 Q250 200 380 130 T680 220" />
            <path className="trace-crimson" d="M200 400 Q320 350 480 380 T700 300" />
          </svg>

          {/* Fragmented documents that converge */}
          <div className="relative w-[min(1100px,92vw)] h-[520px]" style={{ perspective: 1400 }}>
            {/* Connecting network lines (fade in as fragments converge) */}
            <motion.svg
              viewBox="-500 -300 1000 600"
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ opacity: linkOpacity }}
              aria-hidden
            >
              {NODE_POINTS.flatMap((a, i) =>
                NODE_POINTS.slice(i + 1).map((b, j) => (
                  <line
                    key={`${i}-${j}`}
                    x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                    stroke="#00D4FF" strokeWidth="1.5" strokeDasharray="4 6" strokeOpacity="0.65"
                  />
                ))
              )}
              {NODE_POINTS.map((n, i) => (
                <circle key={i} cx={n[0]} cy={n[1]} r="6" fill="#00D4FF" fillOpacity="0.9" />
              ))}
            </motion.svg>

            {/* Fragments */}
            {FRAGMENTS.map((f) => (
              <Fragment key={f.id} f={f} converge={converge} />
            ))}

            {/* Caption that reveals when network forms */}
            <motion.div
              className="absolute left-1/2 top-full -translate-x-1/2 mt-6 text-center pointer-events-none"
              style={{ opacity: captionOpacity }}
            >
              <div className="text-[10px] font-mono tracking-[0.36em] text-gold">NETWORK · FORMED</div>
              <div className="text-white/60 text-xs mt-1">The pieces connect. The picture emerges.</div>
            </motion.div>
          </div>

          {/* Foreground headline */}
          <div className="absolute inset-x-0 top-24 md:top-32 px-6 md:px-12 pointer-events-none">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 border border-white/15 rounded-sm px-3 py-1 text-[10px] font-mono tracking-[0.36em] text-white/70">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> ABOUT · IOCRI
              </div>
              <h1 className="font-display text-4xl md:text-6xl mt-6 text-white leading-[0.98]">
                Every crime <em style={{ color: "#C1121F", fontStyle: "normal" }}>begins</em> in fragments.
              </h1>
              <div className="mt-4 text-white/70 max-w-xl mx-auto">
                Scroll. The evidence assembles itself.
              </div>
            </div>
          </div>

          {/* Scroll cue */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-mono tracking-[0.36em] text-white/40">
            SCROLL ↓
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* MISSION + VISION */}
      {/* ============================================================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="about-mission">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="corner-brackets relative rounded-sm border border-gold/25 bg-black/40 p-8">
            <span className="evidence-chip">MISSION · 01</span>
            <div className="font-display text-2xl md:text-3xl text-white mt-5 leading-tight">
              To empower researchers, policymakers and citizens with open knowledge and AI tools against organized crime.
            </div>
          </div>
          <div className="corner-brackets relative rounded-sm border border-crimson/40 bg-black/40 p-8" style={{ borderColor: "rgba(193,18,31,0.4)" }}>
            <span className="evidence-chip crimson">VISION · 02</span>
            <div className="font-display text-2xl md:text-3xl text-white mt-5 leading-tight">
              A world where communities are informed, victims are supported, and criminal networks are transparent.
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* FOUNDER SPOTLIGHT */}
      {/* ============================================================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-12" data-testid="founder-spotlight">
        <div className="rounded-sm border border-gold/30 bg-gradient-to-br from-gold/[0.04] to-transparent p-6 md:p-10">
          <div className="grid md:grid-cols-[260px,1fr] gap-8 items-center">
            <Link to={`/authors/${FOUNDER.slug}`} data-testid="founder-photo-link" className="block group">
              <div className="relative rounded-sm overflow-hidden border border-gold/40 shadow-[0_20px_60px_-20px_rgba(0,212,255,0.5)]">
                <img src={FOUNDER.photo} alt={FOUNDER.name} className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-700" />
              </div>
            </Link>
            <div>
              <span className="evidence-chip">FOUNDER · IOCRI</span>
              <div className="text-[10px] font-mono tracking-[0.36em] text-gold mt-3">{FOUNDER.role.toUpperCase()}</div>
              <h3 className="font-display text-4xl md:text-5xl text-white mt-2 leading-tight">{FOUNDER.name}</h3>
              <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl">{FOUNDER.bio}</p>
              <Link to={`/authors/${FOUNDER.slug}`} data-testid="founder-readmore" className="inline-block mt-6 text-gold font-mono text-xs tracking-widest link-underline">
                READ FULL PROFILE →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing / Co-Founder — same visual weight as founder */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-8" data-testid="marketing-spotlight">
        <div className="rounded-sm border border-gold/30 bg-gradient-to-br from-gold/[0.04] to-transparent p-6 md:p-10">
          <div className="grid md:grid-cols-[260px,1fr] gap-8 items-center">
            <Link to={`/authors/${MARKETING.slug}`} data-testid="marketing-photo-link" className="block group">
              <div className="relative rounded-sm overflow-hidden border border-gold/40 shadow-[0_20px_60px_-20px_rgba(0,212,255,0.5)]">
                <img src={MARKETING.photo} alt={MARKETING.name} className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-700" />
              </div>
            </Link>
            <div>
              <span className="evidence-chip">CO-FOUNDER · IOCRI</span>
              <div className="text-[10px] font-mono tracking-[0.36em] text-gold mt-3">{MARKETING.role.toUpperCase()}</div>
              <h3 className="font-display text-4xl md:text-5xl text-white mt-2 leading-tight">{MARKETING.name}</h3>
              <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl">{MARKETING.bio}</p>
              <Link to={`/authors/${MARKETING.slug}`} data-testid="marketing-readmore" className="inline-block mt-6 text-gold font-mono text-xs tracking-widest link-underline">
                READ FULL PROFILE →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* CORE VALUES */}
      {/* ============================================================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="about-values">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="evidence-chip">CORE VALUES</span>
            <h2 className="font-display text-3xl md:text-5xl mt-4 text-white leading-[1.02]">
              How the desk operates.
            </h2>
          </div>
          <div className="hidden md:block text-[10px] font-mono tracking-[0.36em] text-white/40">FOUR PRINCIPLES</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {VALUES.map((v) => (
            <div key={v.t} className="corner-brackets relative rounded-sm border border-white/10 bg-black/40 hover:border-gold/40 transition-colors p-6" data-testid={`value-${v.code}`}>
              <div className="text-[10px] font-mono tracking-[0.36em] text-gold">◤ {v.code}</div>
              <div className="font-display text-xl text-white mt-3">{v.t}</div>
              <div className="text-white/60 text-sm mt-2 leading-relaxed">{v.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================= */}
      {/* TIMELINE — vertical scroll trace */}
      {/* ============================================================= */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24" data-testid="about-timeline">
        <span className="evidence-chip">TIMELINE · IOCRI</span>
        <h2 className="font-display text-3xl md:text-5xl mt-4 text-white leading-[1.02] mb-10">
          A short institutional history.
        </h2>
        <div className="relative pl-8">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent" />
          <div className="space-y-8">
            {TIMELINE.map(([y, d], i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative"
              >
                <span className="absolute -left-[27px] top-2 h-3 w-3 rounded-full bg-gold shadow-[0_0_10px_#00D4FF]" />
                <div className="text-[10px] font-mono tracking-[0.36em] text-gold">{y}</div>
                <div className="font-display text-lg md:text-xl text-white mt-1 leading-snug">{d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Fragment({ f, converge }) {
  const x = useTransform(converge, [0, 1], [f.xFrom, f.xTo]);
  const y = useTransform(converge, [0, 1], [f.yFrom, f.yTo]);
  const rot = useTransform(converge, [0, 1], [f.rotFrom, 0]);
  return (
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ x, y, rotate: rot }}
    >
      <div className="w-[168px] px-4 py-3 border border-gold/40 bg-black/70 backdrop-blur-md rounded-sm shadow-[0_10px_40px_-10px_rgba(0,212,255,0.4)]">
        <div className="text-[9px] font-mono tracking-[0.36em] text-gold">FRAGMENT · {String(f.id).padStart(2, "0")}</div>
        <div className="font-display text-lg text-white mt-1 leading-none">{f.label}</div>
        <div className="mt-2 h-px bg-white/10" />
        <div className="mt-2 text-[9px] font-mono text-white/40 tracking-widest">IOCRI · EVIDENCE</div>
      </div>
    </motion.div>
  );
}
